/**
 * useTransferAction - Wraps the bulk update status mutation for entity change cards.
 *
 * Returns handlers to mark event IDs as transferred or skipped,
 * and tracks loading/success state for each action.
 */

import { useCallback, useState } from 'react';

import { useBulkUpdateChangeStatusMutation } from '@/services/api/crossMatterChangesService';

export type TransferStatus = 'idle' | 'transferred' | 'skipped';

export interface TransferAction {
  /** Call to mark the given events as transferred */
  handleTransfer: () => void;
  /** Call to mark the given events as skipped */
  handleSkip: () => void;
  /** Whether a mutation is currently in flight */
  isLoading: boolean;
  /** The resolved status after a successful mutation */
  status: TransferStatus;
}

export function useTransferAction(eventIds: string[]): TransferAction {
  const [bulkUpdate, { isLoading }] = useBulkUpdateChangeStatusMutation();
  const [status, setStatus] = useState<TransferStatus>('idle');

  const handleTransfer = useCallback(() => {
    if (eventIds.length === 0 || isLoading || status !== 'idle') return;
    bulkUpdate({ eventIds, action: 'transferred' })
      .unwrap()
      .then(() => setStatus('transferred'))
      .catch(() => {
        // Error is handled by RTK Query / base query error handling
      });
  }, [eventIds, isLoading, status, bulkUpdate]);

  const handleSkip = useCallback(() => {
    if (eventIds.length === 0 || isLoading || status !== 'idle') return;
    bulkUpdate({ eventIds, action: 'skipped' })
      .unwrap()
      .then(() => setStatus('skipped'))
      .catch(() => {
        // Error is handled by RTK Query / base query error handling
      });
  }, [eventIds, isLoading, status, bulkUpdate]);

  return { handleTransfer, handleSkip, isLoading, status };
}
